import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { DotPattern } from './types';
import { useKTTS } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { getAccommodationTimerDuration, isAccommodationTimerUntimed } from '../../../policies/studentAccommodationPolicy';
import { setUserProgress } from '../../../config/api';
import { K_PRACTICE_TIMER_CONSTANTS, PRACTICE_BADGE_CONFIG, PRACTICE_PHASES } from '../shared';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';

interface FluencyScreenProps {
  settings: KPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}


const FluencyScreen: React.FC<FluencyScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const timerDurationMs = getAccommodationTimerDuration(6000, user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const [currentFact, setCurrentFact] = useState<{ factId: string; digit: number } | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const factScoresRef = useRef(factScores);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);

  // Hidden timer state
  const [timeRemaining, setTimeRemaining] = useState(timerDurationMs);
  const [timerActive, setTimerActive] = useState(false);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const isFirstQuestionRef = useRef(true);

  // Subitizing-specific states
  const [flashPhase, setFlashPhase] = useState<'ready' | 'flashing' | 'blank' | 'question'>('ready');
  const [showDots, setShowDots] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<DotPattern | null>(null);

  const { playTTSWithCallback, playChime, isTTSPlaying } = useKTTS();

  // Quantity to fact mapping (quantities 1-6 map to facts 1623-1628)
  const quantityToFact = useCallback((quantity: number): string => {
    return `FACT${1622 + quantity}`; // 1623-1628 for quantities 1-6
  }, []);

  // Generate dot pattern using 3x3 grid
  const generateDotPattern = useCallback((quantity: number): DotPattern => {
    // 3x3 grid positions - edges and center of the large card area
    const gridPositions = [
      {x: 10, y: 10}, {x: 50, y: 10}, {x: 90, y: 10},  // Top row
      {x: 10, y: 50}, {x: 50, y: 50}, {x: 90, y: 50},  // Middle row
      {x: 10, y: 90}, {x: 50, y: 90}, {x: 90, y: 90}   // Bottom row
    ];

    // Shuffle positions and select the first 'quantity' positions
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Create dots from selected positions
    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots
    };
  }, []);

  // Generate answer choices (target quantity + 3 other options)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Add 3 other numbers between 1-6, avoiding target
    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6) + 1; // 1-6
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const currentDigit = currentFact?.digit;

  // Keep factScoresRef in sync with factScores state
  useEffect(() => {
    factScoresRef.current = factScores;
  }, [factScores]);

  // Initialize first fact when component loads
  useEffect(() => {
    if (!currentFact && factsToPractice.length > 0) {
      setCurrentFact(factsToPractice[0]);
    }
  }, [currentFact, factsToPractice]);

  // Initialize answer choices when digit changes
  useEffect(() => {
    if (currentDigit !== undefined) {
      const newPattern = generateDotPattern(currentDigit);
      setCurrentPattern(newPattern);
      setAnswerChoices(generateAnswerChoices(currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setWrongAnswer(null);
      setTimeRemaining(timerDurationMs);
      setTimerActive(false);
      setFlashPhase('ready');
      setQuestionStartTime(Date.now()); // Reset timer for new question
    }
  }, [currentDigit, generateDotPattern, generateAnswerChoices, timerDurationMs]);

  // Start the subitizing sequence when pattern changes
  useEffect(() => {
    if (currentPattern && flashPhase === 'ready') {
      const startFlash = () => {
        setFlashPhase('flashing');
        setShowDots(true);

        // Flash for K duration (800ms)
        const timeoutId2 = setTimeout(() => {
          setShowDots(false);
          setFlashPhase('blank');

          // Brief blank period, then show question and start timer
          const timeoutId3 = setTimeout(() => {
            setFlashPhase('question');
          }, 100);
          timeoutsRef.current.push(timeoutId3);
        }, K_PRACTICE_TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
        timeoutsRef.current.push(timeoutId2);
      };

      if (isFirstQuestionRef.current) {
        // First question: small delay before flash
        const timeoutId1 = setTimeout(startFlash, 500);
        timeoutsRef.current.push(timeoutId1);
      } else {
        // Subsequent questions: start flash immediately
        startFlash();
      }
    }
  }, [currentPattern, flashPhase]);

  // Play TTS instruction (first question only)
  useEffect(() => {
    if (flashPhase === 'question' && !ttsHasPlayed) {
      if (isFirstQuestionRef.current && settings.speechEnabled && currentDigit !== undefined) {
        // First question: play TTS instruction then start timer
        const audioFile = {
          filename: 'subitizing_instruction.mp3',
          text: 'How many dots did you see?'
        };

        playTTSWithCallback(audioFile, () => {
          setTtsHasPlayed(true);
          setTimerActive(true);
        }).catch(() => {
          setTtsHasPlayed(true);
          setTimerActive(true);
        });
      } else {
        // Subsequent questions or speech disabled: skip TTS, start timer immediately
        setTtsHasPlayed(true);
        setTimerActive(true);
      }
    }
  }, [flashPhase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, currentDigit]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // Hidden 3.35-second countdown timer
  useEffect(() => {
    if (isUntimed || !timerActive || hasAnswered || timeRemaining <= 0) {
      if (timeRemaining <= 0 && !hasAnswered && currentFact) {
        // Time expired - treat as incorrect answer
        setHasAnswered(true);
        setTimerActive(false);
        setTimeRemaining(timerDurationMs); // Reset timer

        // Calculate response time (full timer duration)
        const responseTime = Date.now() - questionStartTime;

        // Report timeout attempt data to progress queue
        if (user?.userId && currentFact) {
          try {
            progressQueue.enqueueProgressUpdate(
              user.userId,
              {
                facts: {
                  [currentFact.factId]: {
                    attempts: 1,
                    correct: 0, // Timeout is incorrect
                    timeSpent: responseTime
                  }
                }
              }
            );
          } catch (error) {
            console.error('Error reporting Pre-K subitizing fluency timeout:', error);
          }
        }

        handleAnswerSubmission(false);
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [isUntimed, timeRemaining, timerActive, hasAnswered, currentFact, factScores, user, currentDigit, quantityToFact, questionStartTime]);

  const advanceToNext = useCallback(() => {
    // Check if all facts have score >= 1 (which means 2 correct answers due to async state)
    const latestScores = factScoresRef.current;
    const eligibleFacts = factsToPractice.filter(fact => {
      const score = latestScores.get(fact.factId) || 0;
      return score < 1; // Changed from < 2 to < 1 due to async state updates
    });

    if (eligibleFacts.length === 0) {
      // All facts completed
      onComplete();
      return;
    }

    // Select a new random fact from eligible facts (score < 2)
    isFirstQuestionRef.current = false;
    const randomIndex = Math.floor(Math.random() * eligibleFacts.length);
    const nextFact = eligibleFacts[randomIndex];
    setCurrentFact(nextFact);

    // Force generate new pattern and choices for the new question
    const newPattern = generateDotPattern(nextFact.digit);
    setCurrentPattern(newPattern);
    setAnswerChoices(generateAnswerChoices(nextFact.digit));

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
    setWrongAnswer(null);
    setTimeRemaining(timerDurationMs);
    setTimerActive(false);
    setShowDots(false);
    setFlashPhase('ready');
    setQuestionStartTime(Date.now()); // Reset timer for new question
  }, [factsToPractice, factScores, onComplete, generateDotPattern, generateAnswerChoices, timerDurationMs]);

  const handleAnswerSubmission = useCallback((isCorrect: boolean) => {
    if (!currentFact) return;

    setHasAnswered(true);
    setTimerActive(false);

    // Calculate response time
    const responseTime = Date.now() - questionStartTime;

    // Report attempt data to progress queue
    if (user?.userId && currentFact) {
      try {
        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [currentFact.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: responseTime
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting Pre-K subitizing fluency attempt:', error);
      }
    }

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Update fact score: +1 for correct
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        // Only increment counter if going from 0 to 1 (first genuine correct)
        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        // Update backend fact status to fluency3Practice when score reaches 1
        // (which represents 2 correct answers due to async state timing)
        if (newScore === 1 && user?.userId) {
          const timeoutId = setTimeout(async () => {
            try {
              const progressUpdate: SetUserProgressRequest = {
                facts: {
                  [currentFact.factId]: {
                    status: 'fluency3Practice'
                  }
                }
              };

              await setUserProgress(user.userId, progressUpdate);
            } catch (error) {
              console.error('Failed to update fact status to fluency3Practice:', error);
            }
          }, 0);
          timeoutsRef.current.push(timeoutId);
        }

        return newScores;
      });

      // Play chime and advance after a delay
      playChime().then(() => {
        const timeoutId = setTimeout(() => {
          advanceToNext();
        }, 500);
        timeoutsRef.current.push(timeoutId);
      });
    } else {
      // Incorrect answer

      // Update fact score: -1 for incorrect
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        // If score goes below 1, decrement counter (was previously counted but now incorrect)
        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      // Reset to allow another attempt after feedback
      const timeoutId = setTimeout(() => {
        setHasAnswered(false);
      }, 1500);
      timeoutsRef.current.push(timeoutId);
    }
  }, [currentFact, playChime, advanceToNext, user, factScores, questionStartTime, currentDigit, quantityToFact]);

  const handleAnswerClick = useCallback((selectedDigit: number) => {
    // Don't allow clicks while TTS is playing or already answered
    if (isTTSPlaying || hasAnswered || !currentFact) return;

    const isCorrect = selectedDigit === currentDigit;

    // Set wrong answer for visual feedback
    if (!isCorrect) {
      setWrongAnswer(selectedDigit);
      const timeoutId = setTimeout(() => setWrongAnswer(null), 1000);
      timeoutsRef.current.push(timeoutId);
    }

    handleAnswerSubmission(isCorrect);
  }, [currentDigit, hasAnswered, isTTSPlaying, currentFact, handleAnswerSubmission]);

  if (!currentPattern || !currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row - Back Button, Practice Indicator and Timer */}
      <div className="flex justify-between items-center p-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
        >
          ← Back
        </button>

        {/* Practice Indicator */}
        <div className={`${PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].bgColor} ${PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].textColor} px-6 py-3 rounded-full font-bold text-lg shadow-lg`}>
          {PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].emoji} {PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].label}
        </div>

        {/* Question Timer - Shows countdown for current question */}
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="2"
            />
            {timerActive && !isUntimed && (
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke={timeRemaining < 1000 ? "#dc2626" : "#f59e0b"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="87.96"
                strokeDashoffset={87.96 * (1 - (timeRemaining / timerDurationMs))}
                className="transition-all duration-100 ease-linear"
              />
            )}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-xs font-bold ${timeRemaining < 1000 ? 'text-red-600' : 'text-yellow-600'}`}>
              {isUntimed ? '∞' : timerActive ? Math.ceil(timeRemaining / 1000) : '⏱️'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">

          {/* Ready Phase: Waiting - only shown on first question */}
          {flashPhase === 'ready' && isFirstQuestionRef.current && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">Get ready...</div>
              <div className="text-lg text-gray-500">Be fast!</div>
            </div>
          )}

          {/* Dots Container - Uses the exact same area as the 4 option cards */}
          {flashPhase === 'flashing' && showDots && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12 relative">
              {/* Create the exact same 4-card layout area */}
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>

              {/* Dots positioned absolutely over this area - 3x3 grid positions */}
              {currentPattern.dots.map((dot) => (
                <motion.div
                  key={dot.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full shadow-lg"
                  style={{
                    left: `${dot.x}%`,
                    top: `${dot.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              ))}
            </div>
          )}

          {/* Blank Phase: Same exact area as cards */}
          {flashPhase === 'blank' && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
            </div>
          )}

          {/* Answer Choices - 2x2 grid */}
          {flashPhase === 'question' && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              {answerChoices.map((choice, index) => (
                <motion.button
                  key={choice}
                  initial={isFirstQuestionRef.current ? { opacity: 0, y: 30, rotate: -5 } : { opacity: 0 }}
                  animate={isFirstQuestionRef.current ? { opacity: 1, y: 0, rotate: 0 } : { opacity: 1 }}
                  transition={isFirstQuestionRef.current ? { duration: 0.4, delay: index * 0.1 } : { duration: 0.15 }}
                  whileHover={{ scale: 1.08, rotate: 1 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => handleAnswerClick(choice)}
                  disabled={hasAnswered}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                    ${wrongAnswer === choice
                      ? 'bg-red-500 text-white border-4 border-red-700 shadow-red-300 animate-pulse'
                      : hasAnswered && choice === currentDigit
                      ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                      : hasAnswered && choice !== currentDigit
                      ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                      : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700'
                    }
                    ${hasAnswered ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {choice}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Quick answer!</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FluencyScreen;
